<template>
	<div class="pb-16">
		<transition name="fade">
			<div v-if="showSaveNotification" class="fixed top-0 left-0 right-0 bg-green-500 text-white py-4 px-4 text-center z-50">Changes saved automatically</div>
		</transition>
		<Mango @update:data="setKit" class="flex flex-col w-full items-center" :query="{ depthLimit: 2 }">
			<Spinner v-if="!store.editingKit?.id" />
			<template v-else-if="store.editingKit?.id">
				<div class="w-full max-w-8xl mb-12">
					<div class="p-4" style="font-family: 'Montserrat', sans-serif">
						<div class="max-w-4xl flex flex-col items-center sm:items-start">
							<UploadFile
								ref="uploadFile"
								@url="store.editingKit.image = $event"
								class="h-[20vh] aspect-video bg-gray-100 cursor-pointer mb-4 flex items-center justify-center relative border-2 rounded-xl shadow-card overflow-hidden"
								:showDelete="false"
								:showProgress="false"
								:cropAspect="16 / 9"
								:cropPrompt="false"
								v-slot="{ progress }"
							>
								<div class="h-4 bg-green-500 absolute left-0 bottom-0" :style="`width: ${progress}%`" />
								<div v-if="!store.editingKit.image" class="w-full h-full flex flex-col items-center justify-center">
									<i class="fa fa-user text-3xl" />
									<div class="mt-2">{{ progress ? 'Uploading...' : 'Upload a Banner Image' }}</div>
								</div>
								<div
									v-else
									class="w-full h-full bg-cover bg-center group relative overflow-hidden z-20"
									:style="`background-image: url('${store.editingKit.image?.url || store.editingKit.image}')`"
								>
									<img :src="store.editingKit.image?.url || store.editingKit.image" class="hidden" @error="imageError = true" @load="imageError = false" />
									<div v-if="imageError" class="w-full h-full flex flex-col items-center justify-center absolute">
										<i class="fa fa-image text-3xl text-gray-400" />
										<div class="mt-2 text-gray-500 text-sm">Image failed to load — click to replace</div>
									</div>
									<div class="w-full h-full hidden group-hover:flex absolute backdrop-blur items-center justify-center">
										<i class="fa fa-pencil text-3xl text-gray-800" />
									</div>
								</div>
								<div
									v-if="store.editingKit.image"
									class="absolute w-full h-full bg-cover bg-center z-10"
									:style="`background-image: url('${store.editingKit.image?.url || store.editingKit.image}')`"
								>
									<div class="absolute w-full h-full backdrop-blur-xl" />
								</div>
							</UploadFile>
							<div v-if="store.editingKit.image" class="flex justify-center gap-3 mb-8">
								<button
									@click="replaceImage"
									class="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
								>
									<i class="fa fa-upload" />Replace Image
								</button>
								<button
									@click="removeImage"
									class="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border border-red-300 text-red-500 hover:bg-red-50 transition-colors"
								>
									<i class="fa fa-trash" />Remove Image
								</button>
							</div>
						</div>

						<div class="mb-12 flex flex-col items-start">
							<div class="uppercase text-sm tracking-widest font-bold text-black/60 mb-2">Birth Kit Name</div>
							<div class="w-full max-w-2xl flex items-center justify-between sm:text-3xl font-semibold text-ihh-midnight-gray border rounded-xl overflow-hidden">
								<input
									ref="nameInput"
									v-model="store.editingKit.name"
									@input="updateInputWidth"
									placeholder="Birth Kit Name"
									:style="{ width: inputWidth + 'px' }"
									class="outline-none py-3 pl-4 pr-1 w-full"
								/>
								<div v-if="!hideCustomBKLabel" class="text-ihh-midnight-gray shrink-0 bg-[#e5e7eb] py-3 pl-1 pr-4 cursor-default">Custom Birth Kit</div>
							</div>
							<div v-if="isAdmin" class="mt-2 text-sm flex items-center">
								<label class="custom-checkbox relative inline-block overflow-hidden w-4 h-4 bg-transparent rounded border border-gray-300 cursor-pointer mr-2">
									<input
										id="hideCustomBKLabel"
										type="checkbox"
										v-model="hideCustomBKLabel"
										@change="updateHideCustomBKLabel"
										class="absolute opacity-0 w-0 h-0"
									/>
									<span class="checkmark absolute inset-0 flex items-center justify-center hidden text-ihh-white/90 text-lg">✓</span>
								</label>
								<label for="hideCustomBKLabel" class="text-ihh-slate-gray text-sm items-center text-start">Hide Custom Birth Kit Label</label>
							</div>
							<div
								v-if="hasOutOfStockItems"
								class="flex mt-6 w-full max-w-52 justify-center items-center text-center text-ihh-white text-xs font-semibold bg-ihh-coral rounded py-2 capitalize"
							>
								Kit contains out of stock items
							</div>
							<button @click="customUrlModalOpen = true" class="mt-4 text-sm text-ihh-dark-salmon hover:text-ihh-salmon underline font-semibold">
								Customize URL
							</button>
							<div v-if="store.editingKit?.customUrl?.url" class="text-xs text-black/40 mt-1">inhishands.com/birthKits/{{ store.editingKit.customUrl.url }}</div>
						</div>

						<div class="tracking-wide text-black/60 whitespace-nowrap uppercase text-sm font-bold">Included in your kit</div>
						<Draggable
							v-model="includedProducts"
							group="products"
							:item-key="(item) => item.product?.id || item.product"
							class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2"
							@add="handleMove($event, false)"
						>
							<template #item="{ element: p }">
								<BirthKitBuilderItem
									:key="p.product?.id || p.product"
									:product="p.product"
									:variant="p.variant"
									:quantity="p.quantity"
									@update:quantity="setQuantity(p, $event)"
									@update:variant="setVariant(p, $event)"
									@remove="setQuantity(p, 0)"
								/>
							</template>
						</Draggable>
						<button
							@click="
								() => {
									addItemModalOpen = true
									store.birthKitOptional = false
								}
							"
							class="text-ihh-midnight-gray font-semibold w-1/2 h-24 bg-ihh-dark-salmon rounded-xl mt-4"
						>
							Add Item +
						</button>

						<div class="mt-16">
							<div class="tracking-wide text-black/60 whitespace-nowrap uppercase text-sm font-bold">Optional items</div>
							<Draggable
								v-model="optionalProducts"
								group="products"
								:item-key="(item) => item.product?.id || item.product"
								class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2"
								@add="handleMove($event, true)"
							>
								<template #item="{ element: p }">
									<BirthKitBuilderItem
										:key="p.product?.id || p.product"
										:product="p.product"
										:variant="p.variant"
										:quantity="p.quantity"
										:optional="true"
										@update:quantity="setQuantity(p, $event)"
										@update:variant="setVariant(p, $event)"
										@remove="setQuantity(p, 0)"
									/>
								</template>
							</Draggable>
							<button
								@click="
									() => {
										addItemModalOpen = true
										store.birthKitOptional = true
									}
								"
								class="text-ihh-midnight-gray font-semibold w-1/2 h-24 bg-ihh-dark-salmon rounded-xl mt-4"
							>
								Add Optional Item +
							</button>
						</div>
					</div>
				</div>

				<div v-if="store.editingKit?.id" class="flex w-full justify-center sticky bottom-0 z-10">
					<div
						class="flex w-full justify-center m-auto items-center w-full bg-ihh-white shadow-md px-4 py-10 sm:p-y rounded border border-ihh-gray/50 cursor-pointer"
					>
						<div class="flex w-full max-w-8xl justify-between">
							<div class="flex flex-col w-full justify-between hidden sm:block">
								<div class="text-2xl font-semibold text-ihh-midnight-gray max-w-md tracking-wide mr-6">{{ store.editingKit.name }}</div>
								<button
									v-if="!store.editingKit.isVisible"
									@click="submitForApproval"
									class="bg-ihh-dark-salmon text-ihh-midnight-gray px-6 py-2 rounded hover:bg-ihh-dark-salmon transition-colors mt-2"
								>
									Submit for Approval
								</button>
								<div v-if="hasOutOfStockItems" class="flex w-full justify-start">
									<button class="mt-2 text-xs text-center bg-ihh-coral py-1 px-3 text-ihh-white font-bold capitalize rounded-md">
										Kit contains out of stock items
									</button>
								</div>
							</div>
							<div class="flex w-full justify-between sm:justify-end items-center">
								<div class="flex-col text-ihh-midnight-gray text-center md:text-end mr-6">
									<div class="flex sm:flex-col md:flex-row">
										<div class="text-base xs:text-lg font-semibold mr-2">SUBTOTAL:</div>
										<div class="text-base xs:text-lg font-semibold tracking-widest">
											{{ subtotal?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}
										</div>
									</div>

									<a @click="cartBirthKit" class="flex w-full justify-between whitespace-nowrap">
										<div class="max-w-32 flex w-full">
											<span class="flex w-full justif-end">
												<InfoIcon />
											</span>
											<button @click="shippingModalOpen = true" class="text-ihh-dark-salmon hover:text-ihh-salmon underline text-xs font-semibold">
												Shipping and returns
											</button>
										</div>
									</a>
								</div>
							</div>
						</div>
					</div>
				</div>
			</template>
		</Mango>

		<Modal
			v-if="shippingModalOpen"
			@hide="
				() => {
					shippingModalOpen = false
					store.birthKitOptional = null
				}
			"
		>
			<div class="prose">
				<h2>Shipping and Returns</h2>
				<p>Shipping cost is based on weight. Just add products to your bag and use the Shipping Calculator to view the different options.</p>
				<p>We offer free shipping on orders over $125 shipping to the lower 48 states</p>
				<p>We want you to be 100% satisfied with your purchase. Most items in new condition can be returned or exchanged within 90 days of purchase.</p>
			</div>
		</Modal>

		<Search :show="addItemModalOpen" @hide="addItemModalOpen = false" :navigate="false" />
		<!-- <Modal v-if="addItemModalOpen" @hide="addItemModalOpen = false; optional: null">
			<div class="prose">
				<h2>Add {{ optional ? 'Optional' : 'Primary' }} Products</h2>
			</div>
		</Modal> -->

		<Modal v-if="customUrlModalOpen" @hide="customUrlModalOpen = false">
			<div class="max-w-lg">
				<h2 class="text-xl font-semibold text-ihh-midnight-gray mb-2">Customize URL</h2>
				<p class="text-sm text-black/50 mb-4">Set a custom URL for your birth kit. Only letters, numbers, and dashes are allowed.</p>
				<div class="flex items-center text-sm text-black/40 mb-1">inhishands.com/birthKits/</div>
				<div class="relative">
					<input
						v-model="customSlugInput"
						@input="debounceCheckSlug"
						placeholder="my-custom-url"
						class="w-full border rounded-lg px-4 py-3 pr-10 outline-none focus:border-ihh-dark-salmon transition-colors"
					/>
					<div v-if="slugCheckLoading" class="absolute right-3 top-1/2 -translate-y-1/2">
						<div class="w-5 h-5 rounded-full border-2 border-gray-200 border-t-ihh-dark-salmon animate-spin"></div>
					</div>
					<div v-else-if="slugCheckResult !== null && customSlugInput" class="absolute right-3 top-1/2 -translate-y-1/2">
						<span v-if="slugCheckResult.available" class="text-green-500 text-lg">✓</span>
						<span v-else class="text-red-500 text-lg">✕</span>
					</div>
				</div>
				<div v-if="slugCheckResult !== null && customSlugInput" class="mt-2 text-sm" :class="slugCheckResult.available ? 'text-green-600' : 'text-red-500'">
					{{ slugCheckResult.message }}
				</div>
				<div class="flex justify-end gap-3 mt-6">
					<button @click="customUrlModalOpen = false" class="px-5 py-2 rounded-lg border text-ihh-midnight-gray hover:bg-gray-50 transition-colors">Cancel</button>
					<button
						@click="saveCustomUrl"
						:disabled="!slugCheckResult?.available || savingCustomUrl"
						class="px-5 py-2 rounded-lg bg-ihh-dark-salmon text-ihh-midnight-gray font-semibold hover:bg-ihh-salmon transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
					>
						{{ savingCustomUrl ? 'Saving...' : 'Save' }}
					</button>
				</div>
			</div>
		</Modal>
	</div>
</template>

<script>
import axios from 'axios'
import Product from '../products/product.vue'
import BirthKitBuilderItem from '../products/birth-kit-builder-item.vue'
import Swal from 'sweetalert2'
import Modal from '../layout/modal.vue'
import Mango from '../../helpers/mango'
import InfoIcon from '../layout/info-icon.vue'
import UploadFile from '../../helpers/uploadFile.vue'
import Search from '../layout/search.vue'
import Draggable from 'vuedraggable'

export default {
	mounted() {
		this.$nextTick(() => {
			this.updateInputWidth()
		})
	},
	components: { Product, BirthKitBuilderItem, Modal, InfoIcon, UploadFile, Search, Draggable },
	inject: ['store'],
	data() {
		return {
			inputWidth: 240,
			showSaveNotification: false,
			shippingModalOpen: false,
			originalKit: null,
			kit: null,
			hasOutOfStockItems: false,
			unsavedChanges: false,
			addItemModalOpen: false,
			saving: false,
			customUrlModalOpen: false,
			customSlugInput: '',
			slugCheckResult: null,
			slugCheckLoading: false,
			slugCheckTimeout: null,
			savingCustomUrl: false,
			searchTimeout: 1000,
			query: {
				depthLimit: 2,
				fields: ['id', 'name', 'image', 'products', 'products.product', 'products.variant', 'products.quantity', 'products.optional'],
			},
			hideCustomBKLabel: false,
			imageError: false,
		}
	},
	watch: {
		'store.editingKit': {
			handler(n, o) {
				console.log('o,n', o?.id, n?.id)
				if (o?.id) this.debounceSaveKit()
			},
			deep: true,
		},
		originalKit: {
			handler(kit) {
				if (kit?.id) {
					if (this.store?.user?.roles?.includes('admin')) {
						// Admins can edit any kit
						return
					}
					if (this.store?.user?.id === kit?.authorId && !!this.store?.user?.id) {
						// Authors can only edit their own kits
						return
					}
					// redirect all others to the kit page
					this.store.editingKit = null
					let kitUrl = kit?.customUrl?.url || `${kit?.slug}` || `${kit?.id}`
					this.$router.push(`/birthKits/${kitUrl}`)
				}
			},
			immediate: true,
			deep: true,
		},
	},
	methods: {
		updateInputWidth() {
			const canvas = document.createElement('canvas')
			const context = canvas.getContext('2d')
			const input = this.$refs.nameInput
			if (!input) return
			const computedStyle = window.getComputedStyle(input)

			context.font = `${computedStyle.fontSize} ${computedStyle.fontFamily}`

			const text = input.value || input.placeholder
			const textWidth = context.measureText(text).width

			// Add padding and some buffer space
			const padding = parseInt(computedStyle.paddingLeft) + parseInt(computedStyle.paddingRight)
			const width = Math.max(0, textWidth + padding + 20)

			this.inputWidth = width
		},
		handleMove(evt, isOptional) {
			console.log('handleMove', evt)
			const itemId = evt.item?.attributes?.['product-id']?.value
			const item = this.store.editingKit.products.find((p) => p.product.id == itemId)
			item.optional = isOptional
		},
		setKit(kit) {
			kit = kit?.birthKit || kit
			this.hideCustomBKLabel = kit?.noCustomBKLabel || false
			if (!kit?.noCustomBKLabel) {
				kit.name = kit.name.replace(/ Custom Birth Kit/gi, '')
			}
			this.originalKit = JSON.parse(JSON.stringify(kit))
			kit.products = kit?.products?.sort((a, b) => a.product.name.localeCompare(b.product.name)) || []
			if (this.store.editingKit?.id == kit.id) return this.saveKit()
			this.store.editingKit = kit

			this.hasOutOfStockItems = this.kitHasOutOfStockProducts(this.originalKit)

			this.$nextTick(() => {
				this.updateInputWidth()
			})
		},
		async saveKit() {
			if (!this.store?.editingKit?.id) return
			this.saving = true
			let data = {
				id: this.store.editingKit.id,
				products: this.store.editingKit.products,
				image: this.store.editingKit.image,
				noCustomBKLabel: this.hideCustomBKLabel,
			}
			if (!this.hideCustomBKLabel) {
				data.name = this.store.editingKit.name.replace(/ Custom Birth Kit/gi, '').trim() + ' Custom Birth Kit'
			} else {
				data.name = this.store.editingKit.name.replace(/ Custom Birth Kit/gi, '').trim()
			}
			data = JSON.parse(JSON.stringify(data))
			data.products = data.products.map((p) => ({
				product: p.product?.id || p.product,
				variant: p.variant?.id || p.variant || null,
				quantity: p.optional ? 0 : p.quantity,
				optional: p.optional,
			}))
			await Mango.birthKits.save(data)
			this.unsavedChanges = false
			this.saving = false
			this.showSaveNotification = true
			setTimeout(() => (this.showSaveNotification = false), 3000)
		},
		setQuantity(p, q) {
			// console.log('p, q', p, q)
			// let product = this.store.editingKit.products.find(c => c.product.id == p.product.id)
			// console.log('product', product)
			if (q > 0) return (p.quantity = q)
			this.store.editingKit.products = this.store.editingKit.products.filter((c) => c.product.id != p.product.id)
		},
		setVariant(p, variantId) {
			let product = this.store.editingKit.products.find((c) => c.product.id === p.product.id)
			if (product) {
				product.variant = variantId
			}
		},
		async submitForApproval() {
			try {
				const response = await axios.post(`${this.store.api}/controllers/birthKit/submitForApproval`, {
					kitId: this.store.editingKit.id,
				})

				let data = response?.data
				if (data?.authorized === false || data?.valid === false) {
					Swal.fire({ text: data?.response || 'There was an error submitting your kit for approval.', icon: 'error' })
					return
				}

				Swal.fire({
					text: 'Your kit has been submitted for approval. Our team will review it and contact you soon.',
					icon: 'success',
				})
			} catch (error) {
				console.error('Error submitting kit for approval:', error)
				let data = error?.response?.data
				Swal.fire({
					text: data?.response || 'There was an error submitting your kit for approval. Please try again later.',
					icon: 'error',
				})
			}
		},
		variantsOutOfStock(product) {
			return product?.variants?.every((v) => v.inventoryLevel <= 0) && product.inventoryTracking === 'variant'
		},
		productAndVariantsOutOfStock(product) {
			return product?.outOfStock || this.variantsOutOfStock(product) || !product?.isVisible
		},
		kitHasOutOfStockProducts(kit) {
			return kit?.products?.some((p) => this.productAndVariantsOutOfStock(p.product))
		},
		// getOutOfStockProducts(kit) {
		// 	return kit?.products?.filter((p) => this.productAndVariantsOutOfStock(p.product))
		// },
		async removeImage() {
			const result = await Swal.fire({
				title: 'Remove Image?',
				text: 'Are you sure you want to remove the banner image?',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#e57373',
				confirmButtonText: 'Remove',
			})
			if (!result.isConfirmed) return
			this.store.editingKit.image = null
			this.imageError = false
		},
		replaceImage() {
			const input = this.$refs.uploadFile?.$el?.querySelector('input[type="file"]')
			if (input) input.click()
		},
		updateHideCustomBKLabel() {
			this.store.editingKit.noCustomBKLabel = this.hideCustomBKLabel
		},
		debounceCheckSlug() {
			this.slugCheckResult = null
			clearTimeout(this.slugCheckTimeout)
			let slug = this.customSlugInput
				.toLowerCase()
				.trim()
				.replace(/[^a-z0-9\-\s]/g, '')
				.replace(/\s/g, '-')
			this.customSlugInput = slug
			if (!slug) return
			this.slugCheckLoading = true
			this.slugCheckTimeout = setTimeout(async () => {
				try {
					let res = await axios.post(`${this.store.api}/controllers/birthKit/checkSlug`, { slug, kitId: this.store.editingKit.id })
					if (this.customSlugInput === slug) {
						this.slugCheckResult = res.data?.response || res.data
					}
				} catch (e) {
					this.slugCheckResult = { available: false, message: 'Error checking availability' }
				} finally {
					this.slugCheckLoading = false
				}
			}, 500)
		},
		async saveCustomUrl() {
			if (!this.slugCheckResult?.available) return
			this.savingCustomUrl = true
			try {
				let Authorization = window.localStorage.getItem('token')
				let res = await axios.post(
					`${this.store.api}/controllers/birthKit/setCustomUrl`,
					{
						kitId: this.store.editingKit.id,
						slug: this.customSlugInput,
					},
					{ headers: { Authorization } },
				)
				let data = res.data?.response || res.data
				if (res.data?.valid !== false && data?.success) {
					this.store.editingKit.customUrl = { url: data.url, isCustomized: true }
					this.customUrlModalOpen = false
					this.customSlugInput = ''
					this.slugCheckResult = null
					Swal.fire({ text: 'Custom URL saved successfully!', icon: 'success' })
				} else {
					Swal.fire({ text: (typeof res.data?.response === 'string' ? res.data.response : null) || 'Failed to save custom URL', icon: 'error' })
				}
			} catch (e) {
				let errData = e.response?.data
				Swal.fire({ text: errData?.response || 'Failed to save custom URL', icon: 'error' })
			} finally {
				this.savingCustomUrl = false
			}
		},
	},
	computed: {
		includedProducts() {
			return this.store.editingKit.products.filter((p) => !p.optional)
		},
		optionalProducts() {
			return this.store.editingKit.products.filter((p) => p.optional)
		},
		debounceSaveKit() {
			return () => {
				this.unsavedChanges = true
				clearTimeout(this.searchTimeout)
				this.searchTimeout = setTimeout(() => {
					this.saveKit()
				}, 1000)
			}
		},
		optionalProducts() {
			return this.store.editingKit?.products?.filter((p) => p.optional && p.product?.id)
		},
		// outOfStock() {
		// 	return this.store.editingKit?.products?.some((p) => {
		// 		p.product?.outOfStock ||
		// 			(p.product?.inventoryTracking === 'variant' && p.variant?.inventoryLevel === 0) ||
		// 			(p.product?.inventoryTracking === 'product' && p.product?.inventoryLevel === 0)
		// 	})
		// },
		// Subtotal calculation (total before tax)
		subtotal() {
			return this.store.editingKit?.products
				?.filter((p) => !p.optional) // Filter out optional products
				?.reduce((total, p) => {
					let price = p.variant?.currentPrice || p.product?.currentPrice || p.variant?.calculatedPrice || p.product?.calculatedPrice
					const rules = Array.isArray(p.product?.bulkPricingRules) ? p.product.bulkPricingRules : []
					for (let rule of rules) {
						// console.log('rule', rule)
						if (p.quantity >= rule.quantityMin && (p.quantity <= rule.quantityMax || !rule.quantityMax)) {
							price = rule.type === 'fixed' ? rule.amount : rule.type === 'price' ? price - rule.amount : price * rule.amount
							// console.log('price', price)
						}
					}
					return total + price * p.quantity
				}, 0)
		},
		// Texas State Sales Tax (8.21% of the subtotal)
		texasStateSalesTax() {
			const taxRate = 0.0821
			return this.subtotal * taxRate
		},
		// Total price including the tax
		totalPrice() {
			const totalWithTax = parseFloat(this.subtotal) + parseFloat(this.texasStateSalesTax)
			return totalWithTax // Return the total with tax formatted as a string
		},
		isAdmin() {
			return this.store?.user?.roles?.includes('admin')
		},
	},
}
</script>

<style>
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
.no-inherit {
	all: initial; /* Resets all properties to their initial values */
}
</style>
